import React from 'react';
import {
  Alert,
  BadgeOutlinedIcon,
  BusinessIcon,
  Button,
  CheckCircleOutlineIcon,
  Divider,
  PersonOutlineIcon,
  PhoneIcon,
  Text,
} from '@billda/design-system';

interface DataSharingConsentFormProps {
  organisationName: string;
  onConsent: () => void;
  onDecline: () => void;
}

const DataSharingConsentForm: React.FC<DataSharingConsentFormProps> = ({
  organisationName,
  onConsent,
  onDecline,
}) => {
  return (
    <div className="mx-auto mt-8 max-w-[600px] rounded-xl border border-border-default bg-surface-default p-8 shadow-lg">
      <div className="mb-6 text-center">
        <BusinessIcon
          aria-hidden="true"
          className="mb-4 size-[60px] text-content-accent"
        />
        <Text
          as="h1"
          variant="h5"
          className="mb-2"
          data-testid="consent-page-title"
        >
          Data Sharing Consent
        </Text>
        <Text as="div" variant="body1" color="secondary">
          Share your profile with {organisationName}?
        </Text>
      </div>

      <Divider className="my-6" />

      <Alert severity="info" className="mb-6">
        You&apos;re already registered with our platform. To join{' '}
        {organisationName}, we need your permission to share your profile
        information with them.
      </Alert>

      <Text as="div" variant="body1" className="mb-2 font-medium">
        Information to be shared:
      </Text>

      <div className="mb-6">
        <div className="flex items-center gap-4 py-2">
          <PersonOutlineIcon
            aria-hidden="true"
            className="size-5 text-content-accent"
          />
          <div className="min-w-0">
            <Text as="div" variant="body1">
              Name
            </Text>
            <Text as="div" variant="body2" color="secondary">
              Your first and last name
            </Text>
          </div>
        </div>
        <div className="flex items-center gap-4 py-2">
          <PhoneIcon
            aria-hidden="true"
            className="size-5 text-content-accent"
          />
          <div className="min-w-0">
            <Text as="div" variant="body1">
              Contact Information
            </Text>
            <Text as="div" variant="body2" color="secondary">
              Your phone number and email address
            </Text>
          </div>
        </div>
        <div className="flex items-center gap-4 py-2">
          <BadgeOutlinedIcon
            aria-hidden="true"
            className="size-5 text-content-accent"
          />
          <div className="min-w-0">
            <Text as="div" variant="body1">
              Business Information
            </Text>
            <Text as="div" variant="body2" color="secondary">
              Your CIS and business details (if provided)
            </Text>
          </div>
        </div>
      </div>

      <Alert
        severity="success"
        icon={<CheckCircleOutlineIcon />}
        className="mb-6"
      >
        Your information will only be shared with {organisationName} and will be
        used in accordance with their privacy policy.
      </Alert>

      <div className="mt-8 flex gap-4">
        <Button
          variant="outline"
          fullWidth
          size="lg"
          onClick={onDecline}
          data-testid="consent-decline-button"
        >
          Decline
        </Button>
        <Button
          variant="neutral"
          fullWidth
          size="lg"
          onClick={onConsent}
          data-testid="consent-accept-button"
        >
          Yes, Share My Information
        </Button>
      </div>

      <Text
        as="div"
        variant="caption"
        color="secondary"
        className="mt-6 block text-center"
      >
        You can manage your data sharing preferences at any time from your
        account settings.
      </Text>
    </div>
  );
};

export default DataSharingConsentForm;
